import { clampSeoDescription } from '../seoDescription.js';
import { CITY_MIN_DISPLAY_COUNT } from './cityThreshold.js';

/**
 * Indexability and copy for filtered /displays/in/... pages. Used by the state
 * and city landing pages and by api/seo-render, so the browser and the crawler
 * HTML agree. Only ?holiday=christmas, ?holiday=halloween and ?type=drive-thru,
 * each alone, can be indexed; every other query string stays noindex,follow.
 */

export const INDEXABLE_HOLIDAYS = { christmas: 'Christmas', halloween: 'Halloween' } as const;
export type IndexableHoliday = keyof typeof INDEXABLE_HOLIDAYS;

type QueryLike = URLSearchParams | Record<string, string | string[] | undefined>;

const queryEntries = (query: QueryLike): Array<[string, string]> =>
  query instanceof URLSearchParams
    ? Array.from(query.entries())
    : Object.entries(query).flatMap(([key, value]) =>
        value === undefined ? [] : [[key, Array.isArray(value) ? value.join(',') : value] as [string, string]],
      );

/** The one indexable holiday filter in the query, or null when it is absent, mixed or a different filter. */
export function indexableHolidayFilter(query: QueryLike): IndexableHoliday | null {
  const entries = queryEntries(query);
  if (entries.length !== 1 || entries[0][0] !== 'holiday') return null;
  const value = entries[0][1].trim().toLowerCase();
  return value in INDEXABLE_HOLIDAYS ? (value as IndexableHoliday) : null;
}

/** An indexable holiday view still needs enough matching displays to be worth indexing. */
export const holidayViewIndexable = (matchCount: number): boolean => matchCount >= CITY_MIN_DISPLAY_COUNT;

/** Title, H1 and description for a holiday-filtered state or city page. `where` is "Texas" or "Austin, TX". */
export function holidayLocationCopy(holiday: IndexableHoliday, where: string, count: number, year: number) {
  const label = INDEXABLE_HOLIDAYS[holiday];
  const h1 = `${label} light displays in ${where}`;
  const withYear = `${h1} (${year})`;
  const title =
    `${withYear} | Light Hunt`.length <= 65 ? `${withYear} | Light Hunt` : `${h1} | Light Hunt`;
  const description = clampSeoDescription(
    `Browse ${count} ${label} light displays in ${where} for the ${year} season, with photos, maps, and directions on Light Hunt.`,
    160,
  );
  return { title, h1, description };
}

/** Indexable ?type= views. Only the canonical spelling; `drive-through` stays a noindex duplicate. */
export const INDEXABLE_TYPES = { 'drive-thru': 'Drive-through' } as const;
export type IndexableType = keyof typeof INDEXABLE_TYPES;

/** One indexable filter on a state or city page: a lone ?holiday= or a lone ?type=. */
export type IndexableLocationView =
  | { param: 'holiday'; value: IndexableHoliday }
  | { param: 'type'; value: IndexableType };

/** The indexable view in the query, or null when it is absent, mixed or not indexable. */
export function indexableLocationView(query: QueryLike): IndexableLocationView | null {
  const holiday = indexableHolidayFilter(query);
  if (holiday) return { param: 'holiday', value: holiday };
  const entries = queryEntries(query);
  if (entries.length !== 1 || entries[0][0] !== 'type') return null;
  const value = entries[0][1].trim().toLowerCase();
  return value in INDEXABLE_TYPES ? { param: 'type', value: value as IndexableType } : null;
}

/** The canonical query string of a view, e.g. `holiday=christmas` or `type=drive-thru`. */
export const locationViewQuery = (view: IndexableLocationView): string => `${view.param}=${view.value}`;

/** Same threshold as the holiday views. */
export const locationViewIndexable = holidayViewIndexable;

/** Title, H1 and description for an indexable filtered state or city page. */
export function locationViewCopy(view: IndexableLocationView, where: string, count: number, year: number) {
  if (view.param === 'holiday') return holidayLocationCopy(view.value, where, count, year);
  const h1 = `${INDEXABLE_TYPES[view.value]} light displays in ${where}`;
  const withYear = `${h1} (${year})`;
  const title =
    `${withYear} | Light Hunt`.length <= 65 ? `${withYear} | Light Hunt` : `${h1} | Light Hunt`;
  const description = clampSeoDescription(
    `Browse ${count} drive-through light displays in ${where} for ${year}: drive-thru shows to see from your car, with photos, maps, and directions on Light Hunt.`,
    160,
  );
  return { title, h1, description };
}
